/**
 * v3.onlineApplications — Phase 2 (Bill 2026-07-01) bridge for the PCB-Flow-owned
 * online account-opening lists at /sites/PCB-AccountOpening (Account + Applicant +
 * Beneficiary, JotForm intake). Sibling of legacyApplications — SAME RAO landing
 * queue, different source channel.
 *
 * Backed by Functions/V3OnlineAppImportFunction.cs:
 *
 *   GET  /v3/admin/import/online-app/available            (?pool=1 admin test)
 *   POST /v3/admin/import/online-app/{accountItemId}/import (?pool=1 ?markClaimed=1)
 *   GET  /v3/admin/import/online-app/{accountItemId}/pdf   (blob — the submission PDF, pre-import)
 *   GET  /v3/carts/{cartId}/imported-pdf                   (blob — side-panel preview)
 *
 * The FSR-facing filter (server-side) is: 711='Y' & Status='New Account', OR
 * Status in (Claimed,Pending) & FSR=caller. `pool` is an admin/pilot affordance
 * that surfaces every online app for testing (requires the Admin role).
 */

import type { RequestOptions } from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';

// -----------------------------------------------------------------------------
// Wire shapes
// -----------------------------------------------------------------------------

export interface OnlineApplicationSummary {
  /** SharePoint Account list item id (string-encoded number). */
  readonly accountItemId: string;
  readonly accountName: string | null;
  readonly product: string | null;
  readonly branch: string | null;
  readonly status: string | null;
  readonly fsr: string | null;
  readonly primaryApplicant: string | null;
  readonly applicantCount: number;
  readonly beneficiaryCount: number;
  readonly hasPdfLink: boolean;
  readonly submissionId: string | null;
}

export interface OnlineApplicationsListResponse {
  readonly success: boolean;
  readonly caller?: string;
  readonly pool?: boolean;
  readonly count?: number;
  readonly apps?: ReadonlyArray<OnlineApplicationSummary>;
  /** Present when listed with a view: whether the view applied (Bill 2026-10-16). */
  readonly team?: { readonly hasTeam: boolean; readonly view?: 'team' | 'all'; readonly basis: string };
  readonly error?: string;
  readonly code?: string;
}

export interface OnlineApplicationImportResponse {
  readonly success: boolean;
  readonly cartId?: string;
  readonly alreadyImported?: boolean;
  readonly peopleCount?: number;
  readonly beneficiaryCount?: number;
  readonly productMatched?: boolean;
  readonly pdfAttached?: boolean;
  readonly markedClaimed?: boolean;
  readonly error?: string;
  readonly code?: string;
}

// -----------------------------------------------------------------------------
// Client
// -----------------------------------------------------------------------------

export class OnlineApplicationsApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * List importable online applications. Default = the caller's own queue
   * (per the server filter). `pool: true` (admin only) surfaces every online
   * app for pilot testing; `fsrEmail` lets an admin preview another FSR's queue.
   */
  public list(
    filters: { pool?: boolean; fsrEmail?: string; view?: 'team' | 'all' } = {},
    opts?: RequestOptions,
  ): Promise<OnlineApplicationsListResponse> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (filters.pool) query.pool = 1;
    if (filters.view) query.view = filters.view;
    if (filters.fsrEmail) query.fsrEmail = filters.fsrEmail;

    return this.http.get<OnlineApplicationsListResponse>('/v3/admin/import/online-app/available', {
      ...opts,
      query,
    });
  }

  /**
   * Import (preload a draft cart). Idempotent on the source app — re-calling
   * returns the existing cart with `alreadyImported=true`. `markClaimed` writes
   * Status=Claimed + FSR back to the legacy row (default off; pilot runs parallel).
   */
  public import(
    accountItemId: string,
    body: { pool?: boolean; markClaimed?: boolean } = {},
    opts?: RequestOptions,
  ): Promise<OnlineApplicationImportResponse> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (body.pool) query.pool = 1;
    if (body.markClaimed) query.markClaimed = 1;

    return this.http.post<OnlineApplicationImportResponse>(
      `/v3/admin/import/online-app/${encodeURIComponent(accountItemId)}/import`,
      { ...opts, query, body: {} },
    );
  }

  /**
   * Fetch the imported JotForm Application PDF for a cart as a Blob, for the
   * side-panel preview. Wrap with `URL.createObjectURL(blob)` and feed an
   * <iframe>/<embed> (iframes can't send auth headers). Rejects with ApiError
   * (404) when the cart has no imported PDF.
   */
  /**
   * The signed application-submission PDF for an application still sitting in
   * the intake queue (before import). Same access filter as list(). Rejects
   * with ApiError 404 when the submission has no PDF linked.
   */
  public applicationPdfBlob(
    accountItemId: string,
    filters: { pool?: boolean } = {},
    opts?: RequestOptions,
  ): Promise<Blob> {
    const query: Record<string, string | number | boolean | undefined> = {};
    if (filters.pool) query.pool = 1;
    return this.http.getBlob(`/v3/admin/import/online-app/${encodeURIComponent(accountItemId)}/pdf`, {
      ...opts,
      query,
    });
  }

  public importedPdfBlob(cartId: string, opts?: RequestOptions): Promise<Blob> {
    return this.http.getBlob(`/v3/carts/${encodeURIComponent(cartId)}/imported-pdf`, { ...opts });
  }
}
